import type {Content} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import dynamic from 'next/dynamic'
import type {FC, ReactNode} from 'react'
import {useEffect, useRef, useState} from 'react'
import {ToastProvider, useToasts} from 'react-toast-notifications'
import {Container} from 'src/common/Container'
import type {FormData} from './components/constants'
import {CATEGORY_OPTIONS, eyebrow} from './components/constants'
import {DownloadDialog} from './components/DownloadDialog'
import {StepTone} from './components/FormPanel/StepTone'
import {TwoPanelLayout} from './components/TwoPanelLayout'
import type {PolicySection} from './hooks/usePolicyGenerator'
import {usePolicyGenerator} from './hooks/usePolicyGenerator'
import {usePolicyGeneratorTracking} from './hooks/usePolicyGeneratorTracking'
import {exportPolicyPdf} from './utils/exportPdf'

export const EXPENSE_POLICY_GENERATOR_ID = 'expense-policy-generator'

const SESSION_KEY = 'epg_session'
const INTRO_KEY = 'epg_intro'

type EpgIntro = Pick<FormData, 'companyName' | 'industry' | 'companySize' | 'country'>

function readIntro(): EpgIntro | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = localStorage.getItem(INTRO_KEY)
    return raw ? (JSON.parse(raw) as EpgIntro) : null
  } catch {
    return null
  }
}

function saveIntro(data: EpgIntro) {
  try {
    localStorage.setItem(INTRO_KEY, JSON.stringify(data))
  } catch {
    // localStorage unavailable — silently skip
  }
}

type EpgSession = {
  formData: FormData
  sections: PolicySection[]
  step: number
}

const DEFAULT_FORM_DATA: FormData = {
  companyName: '',
  industry: '',
  companySize: '',
  country: '',
  riskTolerance: 'balanced',
  guidelinesStyle: 'empowering',
  categories: [],
}

function readSession(): EpgSession | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as EpgSession) : null
  } catch {
    return null
  }
}

function saveSession(session: EpgSession) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    // sessionStorage unavailable (e.g. private mode quota exceeded) — silently skip
  }
}

const StepIntro = dynamic(() => import('./components/FormPanel/StepIntro').then((m) => m.StepIntro), {ssr: false})

export type ExpensePolicyGeneratorProps = SliceComponentProps<Content.ExpensePolicyGeneratorSlice> & {
  context: {pageUid: string}
}

const allCategories = CATEGORY_OPTIONS.flatMap((g) => g.categories)
const getCategoryLabel = (id: string) => allCategories.find((c) => c.value === id)?.label ?? id

const ExpensePolicyGeneratorInner: FC<ExpensePolicyGeneratorProps> = ({slice, context}) => {
  const {addToast} = useToasts()
  const tracking = usePolicyGeneratorTracking()
  const [step, setStep] = useState(0)
  const [downloadOpen, setDownloadOpen] = useState(false)
  const [pdfGenerating, setPdfGenerating] = useState(false)

  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA)

  const {
    requestGroupId,
    sections,
    setSections,
    loadingCategoryIds,
    generating,
    generated,
    rewriting,
    generate,
    updateAllSections,
    canUndo,
    canRedo,
    undo,
    redo,
    pushSnapshot,
    prefetch,
    generatePreamble,
    updateCache,
    reset,
    restore,
    error: policyError,
  } = usePolicyGenerator()

  // Stable ref to restore so the mount effect doesn't need it as a dependency
  const restoreRef = useRef(restore)
  restoreRef.current = restore

  // Restore state on mount (runs client-side only, after hydration).
  // localStorage intro fields are loaded first; sessionStorage overwrites if a full session exists.
  useEffect(() => {
    const intro = readIntro()

    if (intro) {
      setFormData((prev) => ({...prev, ...intro}))
    }

    const saved = readSession()

    if (!saved) {
      return
    }

    setFormData(saved.formData)
    setStep(saved.step)

    if (saved.sections.length > 0) {
      restoreRef.current(saved.sections)

      lastGeneratedParamsRef.current = {
        industry: saved.formData.industry,
        companySize: saved.formData.companySize,
        country: saved.formData.country,
        riskTolerance: saved.formData.riskTolerance,
        guidelinesStyle: saved.formData.guidelinesStyle,
      }

      document.getElementById(EXPENSE_POLICY_GENERATOR_ID)?.scrollIntoView({behavior: 'smooth'})
    }
  }, [])

  // Stable ref so tracking effects don't need it as a dependency (identity changes on every render)
  const trackingRef = useRef(tracking)
  trackingRef.current = tracking

  // Track flow started once on mount
  useEffect(() => {
    trackingRef.current.trackFlowStarted()
  }, [])

  // Track step viewed whenever step changes (skip initial 0 to avoid double-fire with flow_started)
  const isFirstStepTrackRef = useRef(true)

  // Track sg_flow_step_viewed each time the user moves to a new step
  useEffect(() => {
    if (isFirstStepTrackRef.current) {
      isFirstStepTrackRef.current = false
      return
    }

    trackingRef.current.trackStepViewed(step)
  }, [step])

  // Skip the first run of both save effects — on mount, state is still DEFAULT_FORM_DATA
  // and the restore effect hasn't applied storage values yet. Saving at that point
  // would overwrite localStorage/sessionStorage with empty defaults.
  const introSaveReadyRef = useRef(false)
  const sessionSaveReadyRef = useRef(false)

  // Persist intro fields to localStorage so they survive across browser sessions
  useEffect(() => {
    if (!introSaveReadyRef.current) {
      introSaveReadyRef.current = true
      return
    }

    saveIntro({
      companyName: formData.companyName,
      industry: formData.industry,
      companySize: formData.companySize,
      country: formData.country,
    })
  }, [formData.companyName, formData.industry, formData.companySize, formData.country])

  // Persist full session to sessionStorage whenever relevant state changes
  useEffect(() => {
    if (!sessionSaveReadyRef.current) {
      sessionSaveReadyRef.current = true
      return
    }

    saveSession({formData, sections, step})
  }, [formData, sections, step])

  const update = (fields: Partial<FormData>) => setFormData((s) => ({...s, ...fields}))

  // Fields that affect generated content (excluding companyName which is display-only)
  const lastGeneratedParamsRef = useRef<{
    industry: string
    companySize: string
    country: string
    riskTolerance: string
    guidelinesStyle: string
  } | null>(null)

  const goNext = (fields?: Partial<FormData>) => {
    if (fields) {
      update(fields)
    }

    tracking.trackStepCompleted(step)
    setStep((s) => s + 1)
  }

  const goBack = () => {
    tracking.trackStepBack(step, Math.max(0, step - 1))
    setStep((s) => Math.max(0, s - 1))
  }

  const onGenerate = () => {
    const paramsChanged =
      generated &&
      lastGeneratedParamsRef.current !== null &&
      (lastGeneratedParamsRef.current.industry !== formData.industry ||
        lastGeneratedParamsRef.current.companySize !== formData.companySize ||
        lastGeneratedParamsRef.current.country !== formData.country ||
        lastGeneratedParamsRef.current.riskTolerance !== formData.riskTolerance ||
        lastGeneratedParamsRef.current.guidelinesStyle !== formData.guidelinesStyle)

    const catsToRegen = paramsChanged ? formData.categories : []

    if (paramsChanged) {
      reset()
    }

    lastGeneratedParamsRef.current = {
      industry: formData.industry,
      companySize: formData.companySize,
      country: formData.country,
      riskTolerance: formData.riskTolerance,
      guidelinesStyle: formData.guidelinesStyle,
    }

    tracking.trackStepCompleted(step, `${formData.riskTolerance}/${formData.guidelinesStyle}`)
    tracking.trackCategoriesConfirmed(formData.categories)

    void generatePreamble(formData, () => {
      addToast("Our AI is overloaded right now — some sections couldn't be generated. Please try again.", {
        appearance: 'warning',
      })
    })

    if (catsToRegen.length > 0) {
      void generate(
        {...formData, categories: catsToRegen},
        catsToRegen,
        (failedIds) => {
          setFormData((prev) => ({...prev, categories: prev.categories.filter((c) => !failedIds.includes(c))}))
          const labels = failedIds.map(getCategoryLabel).join(', ')

          addToast(`Our AI is overloaded right now — ${labels} couldn't be generated. Please try again.`, {
            appearance: 'warning',
          })
        },
        true /* forceRefresh — ignore stale cached sections */,
      )
    }

    setStep(2)
  }

  const onCategoriesChange = (cats: string[]) => {
    const prevCats = formData.categories
    update({categories: cats})

    const removed = prevCats.filter((c) => !cats.includes(c))
    const added = cats.filter((c) => !prevCats.includes(c))

    if (removed.length > 0) {
      // Sync refined versions to cache before removing so re-adding shows latest content
      updateCache(sections.filter((s) => removed.includes(s.id)))
      setSections((prev) => prev.filter((s) => cats.includes(s.id) || s.id === 'intro' || s.id === 'summary'))
    }

    if (added.length > 0) {
      tracking.trackAiGenerationStarted(added)

      generate({...formData, categories: cats}, cats, (failedIds) => {
        setFormData((prev) => ({...prev, categories: prev.categories.filter((c) => !failedIds.includes(c))}))
        tracking.trackAiGenerationFailed(failedIds)

        const labels = failedIds.map(getCategoryLabel).join(', ')

        addToast(`Our AI is overloaded right now — ${labels} couldn't be generated. Please try again.`, {
          appearance: 'warning',
        })
      })
    }
  }

  const steps: ReactNode[] = [
    <StepIntro
      key="intro"
      data={formData}
      onNext={goNext}
      onChange={update}
      onCompanyBlur={(val) => update({companyName: val})}
    />,
    <StepTone key="tone" data={formData} onGenerate={onGenerate} onBack={goBack} onChange={update} />,
  ]

  return (
    <Container
      backgroundColor="Grey/100"
      className="text-moss-black"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      id={EXPENSE_POLICY_GENERATOR_ID}
    >
      <div className="mx-auto w-full max-w-[1200px] px-5 md:px-10 lg:px-5 xl:px-0">
        <span className={`${eyebrow} mb-4`}>Expense policy generator</span>
        <TwoPanelLayout
          step={step}
          steps={steps}
          total={2}
          sections={sections}
          loadingCategoryIds={loadingCategoryIds}
          generating={generating}
          generated={generated}
          rewriting={rewriting}
          formData={formData}
          onUpdateSections={(instruction, sectionsList) => {
            if (sectionsList.length === 1) {
              tracking.trackCategoryEdited(sectionsList[0].id, instruction.length)
            }

            return updateAllSections(instruction, sectionsList)
          }}
          onCategoriesChange={onCategoriesChange}
          onPrefetchCategory={(cat) => prefetch(cat, formData)}
          onBack={goBack}
          onDownload={() => {
            const categorySections = sections.filter((s) => s.id !== 'intro' && s.id !== 'summary')

            tracking.trackFinishClicked(
              categorySections.length,
              categorySections.filter((s) => s.policyText.trim()).length,
            )

            setDownloadOpen(true)
          }}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          onPushSnapshot={pushSnapshot}
          error={policyError}
        />

        {downloadOpen && (
          <DownloadDialog
            pageUid={context.pageUid}
            companyName={formData.companyName}
            formData={{
              industry: formData.industry,
              companySize: formData.companySize,
              country: formData.country,
            }}
            sections={sections}
            requestGroupId={requestGroupId}
            onClose={() => setDownloadOpen(false)}
            onLocalPdf={async () => {
              if (pdfGenerating) {
                return
              }

              setPdfGenerating(true)

              const orderedIds = ['intro', ...formData.categories, 'summary'].filter((id) =>
                sections.some((s) => s.id === id),
              )

              try {
                await exportPolicyPdf(sections, formData, orderedIds)
                tracking.trackPdfDownloaded(formData.categories.length)
              } catch (err) {
                console.error('PDF generation failed', err)
              } finally {
                setPdfGenerating(false)
              }
            }}
            pdfGenerating={pdfGenerating}
            onDialogViewed={tracking.trackDownloadDialogViewed}
            onDialogDismissed={tracking.trackDownloadDialogDismissed}
            onSubmitClicked={tracking.trackDownloadDialogSubmitClicked}
            onEmailSubmitted={tracking.trackEmailSubmitted}
            onEmailRejected={tracking.trackEmailRejected}
          />
        )}
      </div>
    </Container>
  )
}

const ExpensePolicyGenerator: FC<ExpensePolicyGeneratorProps> = (props) => (
  <ToastProvider autoDismiss={5000}>
    <ExpensePolicyGeneratorInner {...props} />
  </ToastProvider>
)

export default ExpensePolicyGenerator
